/**
 * Четыре величины верхней сводки МСК Газобетона: материалы, доставка, разгрузка
 * и всё остальное из услуг.
 *
 * Своей арифметики нет — раскладываются уже посчитанные суммы строк КП. Задача
 * одна: показать разгрузку отдельной величиной даже тогда, когда в самом КП она
 * зашита в строку рейса манипулятора («Доставка манипулятором с разгрузкой»).
 * Иначе менеджер видел «Разгрузка —», а дорожала почему-то доставка.
 *
 * Сумма трёх величин всегда равна итогу КП: сколько вычли из доставки, столько
 * же добавили в разгрузку.
 */

import { buildGazProposalTotals } from './gazProposalTotals.js';

// Строка рейса, в цену которой уже включена разгрузка собственного груза
// манипулятора. Ключ тот же, что заводит buildGazCommercialProposalBlocks.
const MANIPULATOR_TRIP_WITH_UNLOADING_KEY = 'secondary-manipulator-trip';

// Рейсы, которые везут груз шаландой. Ключи те же, что заводит
// buildGazCommercialProposalBlocks; рейсы манипулятора сюда намеренно не входят —
// показатель говорит о цене куба при погрузке шаланды.
const SHALANDA_TRIP_KEYS = new Set(['main-shalanda', 'secondary-shalanda']);

const positive = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const hasRow = (blocks, key) => (Array.isArray(blocks) ? blocks : []).some(
  (block) => (Array.isArray(block?.rows) ? block.rows : []).some((row) => row?.key === key)
);

/**
 * @param {object} params
 * @param {Array}  params.blocks         — блоки КП того варианта, который показан
 * @param {object} params.totals         — результат buildGazProposalTotals по ним же
 * @param {number} [params.unloadingCost] — разгрузка из серверного deliveryBreakdown
 * @returns {{materialsTotal:number, deliveryTotal:number, unloadingTotal:number,
 *           otherServiceRows:Array}|null}
 */
export const buildGazSummaryMetrics = ({ blocks, totals, unloadingCost } = {}) => {
  if (!totals) {
    return null;
  }

  // Разгрузка отдельной строкой услуги: работа манипулятора или автокран.
  const serviceUnloading = totals.servicesAreUnloadingOnly ? positive(totals.servicesTotal) : 0;

  // Разгрузка, зашитая в рейс манипулятора. Считается только когда этот рейс
  // действительно есть в показанном варианте: у «только полных машин» машины
  // остатка нет, и вычитать из её доставки нечего.
  const bakedUnloading = hasRow(blocks, MANIPULATOR_TRIP_WITH_UNLOADING_KEY)
    ? positive(unloadingCost)
    : 0;

  return {
    materialsTotal: positive(totals.materialsTotal),
    deliveryTotal: Math.max(positive(totals.deliveryTotal) - bakedUnloading, 0),
    unloadingTotal: serviceUnloading + bakedUnloading,
    // Услуги, которые разгрузкой не являются, в её величину не сваливаются.
    otherServiceRows: totals.servicesAreUnloadingOnly ? [] : (totals.serviceRows || []),
  };
};

/** Все рейсы варианта — шаландами: манипулятор груз этого варианта не везёт. */
const isShalandaOnlyHaul = (blocks) => {
  const trips = (Array.isArray(blocks) ? blocks : []).flatMap(
    (block) => (Array.isArray(block?.rows) ? block.rows : [])
      .filter((row) => String(row?.unit || '').trim().toLowerCase() === 'рейс')
  );
  return trips.length > 0 && trips.every((row) => SHALANDA_TRIP_KEYS.has(String(row?.key || '')));
};

/**
 * Основание цены за м³ с доставкой при погрузке шаланды.
 *
 * Показатель отвечает на вопрос «во что обходится куб, когда груз везёт шаланда»,
 * поэтому берётся не выбранный вариант заказа: переключение «Весь объём» ↔ «Только
 * полные машины» его не двигает. При смене производства варианты пересобираются, и
 * показатель пересчитывается сам.
 *
 * Вариант выбирается так: сначала полные машины, а если полностью загруженной
 * шаланды в заказе нет — тот вариант, который едет на самом деле. Недогруженная
 * шаланда всё равно везёт рейс целиком и стоит как рейс, поэтому цена куба по ней
 * считается, а не прячется за прочерком: заказ из одной машины на 95% — обычное
 * дело, и прочерк в нём читался как поломка.
 *
 * Считается только по шаландам: если хоть один рейс варианта манипуляторный,
 * приписать его доставку шаланде нельзя — возвращается null.
 *
 * Своей арифметики заказа здесь нет: блоки выбранного варианта раскладываются теми
 * же функциями, что и величины сводки. Разгрузка в основание не входит — берутся
 * материалы и доставка, из которой уже вычтена разгрузка, зашитая в рейс. Наличное
 * округление не применяется: оно определено на итоге всего варианта вместе с
 * разгрузкой и по разделам не делится.
 *
 * @param {object} params
 * @param {Array}  params.options         — варианты из buildGazSendOptions
 * @param {number} [params.unloadingCost] — разгрузка из серверного deliveryBreakdown
 * @returns {{payableTotal:number, volumeM3:number}|null}
 *   null — шаланда груз не везёт либо её объём нулевой; сводка покажет «—».
 */
export const buildGazFullTruckBasis = ({ options, unloadingCost } = {}) => {
  const list = Array.isArray(options) ? options : [];
  const carrying = list.find((option) => option?.isFullVehiclesOnly)
    || list.find((option) => option && !option.isPartialOrder)
    || null;
  if (!carrying || !isShalandaOnlyHaul(carrying.blocks)) {
    return null;
  }

  const totals = buildGazProposalTotals(carrying.blocks);
  const view = buildGazSummaryMetrics({ blocks: carrying.blocks, totals, unloadingCost });
  const volumeM3 = positive(carrying.volume);
  if (!view || !volumeM3) {
    return null;
  }

  return { payableTotal: view.materialsTotal + view.deliveryTotal, volumeM3 };
};

export default buildGazSummaryMetrics;
